import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import FlyingBirds from "./FlyingBirds";
import ThreeBackground from "./ThreeBackground";

const HeroSection = ({ nightMode, setNightMode }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [luxuryHovered, setLuxuryHovered] = useState(false);
  const [nextHovered, setNextHovered] = useState(false);
  const [bubbleHovered, setBubbleHovered] = useState(false);
  const [triggerBirds, setTriggerBirds] = useState(0);
  const [currentLanguage, setCurrentLanguage] = useState(0);
  const [pinkNoiseOpen, setPinkNoiseOpen] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState('Paris');
  const [timezoneDropdownOpen, setTimezoneDropdownOpen] = useState(false);

  const timezones = {
    'Paris': { iana: 'Europe/Paris' },
    'New York': { iana: 'America/New_York' },
    'Tokyo': { iana: 'Asia/Tokyo' },
    'London': { iana: 'Europe/London' },
    'Dubai': { iana: 'Asia/Dubai' },
    'Mumbai': { iana: 'Asia/Kolkata' },
    'Los Angeles': { iana: 'America/Los_Angeles' },
    'Sydney': { iana: 'Australia/Sydney' }
  };

  const luxuryTranslations = [
    "luxury",
    "luxe",        // French
    "lusso",       // Italian
    "lujo",        // Spanish
    "奢侈品",       // Chinese
    "贅沢",        // Japanese
    "роскошь",     // Russian
    "luxus",       // German
    "विलासिता",    // Hindi
    "사치",        // Korean
  ];

  useEffect(() => {
    setIsLoaded(true);
    
    // Update time every second
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Rotate luxury translations when hovered - faster
    let langTimer;
    if (luxuryHovered) {
      langTimer = setInterval(() => {
        setCurrentLanguage((prev) => (prev + 1) % luxuryTranslations.length);
      }, 800);
    } else {
      setCurrentLanguage(0); // Reset to "luxury" when not hovered
    }

    return () => {
      clearInterval(timer);
      if (langTimer) clearInterval(langTimer);
    };
  }, [luxuryHovered, luxuryTranslations.length]);

  const handleBubbleHover = () => {
    setBubbleHovered(true);
  };

  const getTimezoneTime = () => {
    const tz = timezones[selectedTimezone];
    const tzTime = new Date(currentTime.toLocaleString('en-US', { timeZone: tz.iana }));
    const hours = String(tzTime.getHours()).padStart(2, '0');
    const minutes = String(tzTime.getMinutes()).padStart(2, '0');
    const seconds = String(tzTime.getSeconds()).padStart(2, '0');

    // Get timezone abbreviation
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz.iana,
      timeZoneName: 'short'
    });
    const parts = formatter.formatToParts(currentTime);
    const label = parts.find(part => part.type === 'timeZoneName')?.value || '';

    return { hours, minutes, seconds, label };
  };

  const timeData = getTimezoneTime();

  return (
    <section id="home" className={`hero-luxury ${isLoaded ? "loaded" : ""} ${nightMode ? "night-mode" : ""}`}>
      {/* Wormhole Background */}
      <ThreeBackground />

      {/* Flying Birds Animation */}
      <FlyingBirds triggerFlight={triggerBirds} />

      {/* Top Right - Time Widget */}
      <div className="time-widget-modern">
        <div className="time-main">
          <span className="time-hours">{timeData.hours}</span>
          <span className="time-separator">:</span>
          <span className="time-minutes">{timeData.minutes}</span>
          <span className="time-seconds">:{timeData.seconds}</span>
        </div>
        <div className="timezone-info">
          <div className="timezone-location">Based in Paris</div>
          <div className="custom-dropdown">
            <button
              className="timezone-dropdown-modern"
              onClick={() => setTimezoneDropdownOpen(!timezoneDropdownOpen)}
            >
              {timeData.label}
              <span className="dropdown-arrow">▾</span>
            </button>
            {timezoneDropdownOpen && (
              <div className="custom-dropdown-menu">
                {Object.keys(timezones).map((tz) => (
                  <div
                    key={tz}
                    className={`custom-dropdown-item ${selectedTimezone === tz ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedTimezone(tz);
                      setTimezoneDropdownOpen(false);
                    }}
                  >
                    {tz}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button className="night-mode-toggle" onClick={() => setNightMode(!nightMode)}>
            <span className={nightMode ? 'sun-icon' : 'moon-icon'}>
              {nightMode ? '☀' : '🌙'}
            </span>
          </button>
        </div>
      </div>

      {/* Center Right - Hero Tagline */}
      <div className="hero-tagline-wrapper">
        <h1 className="hero-tagline-luxury">
          <div className="tagline-line">
            decoding{" "}
            <span
              className="luxury-word"
              onMouseEnter={() => setLuxuryHovered(true)}
              onMouseLeave={() => setLuxuryHovered(false)}
              style={{
                display: 'inline-block',
                verticalAlign: 'baseline',
                minWidth: '250px',
                textAlign: 'left'
              }}
            >
              <AnimatePresence mode="wait">
                <motion.span
                  key={currentLanguage}
                  initial={{ opacity: 0, y: 10, filter: 'blur(4px)' }}
                  animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, y: -10, filter: 'blur(4px)' }}
                  transition={{
                    duration: 0.4,
                    ease: [0.4, 0.0, 0.2, 1] // luxury ease curve
                  }}
                  style={{ display: 'inline-block' }}
                >
                  {luxuryTranslations[currentLanguage]}
                </motion.span>
              </AnimatePresence>
            </span>
          </div>
          <div className="tagline-line">
            defining what's{" "}
            <span 
              className="next-word"
              onMouseEnter={() => setNextHovered(true)}
              onMouseLeave={() => setNextHovered(false)}
            >
              next
            </span>
          </div>
        </h1>

        {/* Next Popup */}
        <div className={`next-popup ${nextHovered ? "visible" : ""}`}>
          <div className="next-popup-section">
            <div className="next-popup-label">Next Project</div>
            <div className="next-popup-text">Luxury campaign for Paris Fashion Week 2025</div>
          </div>
          <div className="next-popup-divider"></div>
          <div className="next-popup-section">
            <div className="next-popup-label">Next Blog</div>
            <div className="next-popup-text">Fashion Week 2025 reflections & insights</div>
          </div>
        </div>
      </div>

      {/* Left Side - Static Pink Bubble (non-interactive) */}
      <div className="static-bubble"></div>

      {/* Right Side - Pink Noise GIF */}
      <div
        className="pink-noise-gif-container"
        onMouseEnter={() => setPinkNoiseOpen(true)}
        onMouseLeave={() => setPinkNoiseOpen(false)}
      >
        <img src="/pinknoise.gif" alt="Pink Noise" className="pink-noise-gif" />
        <div className="pink-noise-label">Pink Noise</div>
      </div>

      {/* Pink Noise Popup - Elegant Card */}
      <AnimatePresence>
        {pinkNoiseOpen && (
          <motion.div
            className="pink-noise-popup-elegant"
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 25,
            }}
          >
            {/* Animated background gradient orbs */}
            <motion.div
              className="popup-orb popup-orb-1"
              animate={{
                x: [0, 15, 0],
                y: [0, -20, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <motion.div
              className="popup-orb popup-orb-2"
              animate={{
                x: [0, -20, 0],
                y: [0, 15, 0],
                scale: [1, 1.15, 1],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />

            {/* Content */}
            <motion.div
              className="popup-content"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
            >
              <motion.div
                className="popup-quote-mark"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              >
                "
              </motion.div>
              <motion.p
                className="popup-text"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
              >
                Fashion Week felt like speed-dating with culture.
              </motion.p>
              <motion.div
                className="popup-quote-mark popup-quote-mark-end"
                initial={{ scale: 0, rotate: 180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              >
                "
              </motion.div>
            </motion.div>

            {/* Shimmer effect */}
            <motion.div
              className="popup-shimmer"
              animate={{
                x: ['-100%', '200%'],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                repeatDelay: 2,
                ease: "easeInOut",
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default HeroSection;